import { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { STAGES, STAGE_LABEL, canSeeAmounts, canSeeBillingName, normalizeStage } from '@/lib/roles';
import { MaskedAmount } from '@/components/common/MaskedAmount';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { fmtDate, fmtDateTime } from '@/lib/format';
import { CheckCircle2, PlayCircle, Star, Lock, ExternalLink, Link as LinkIcon, MessageSquarePlus, FolderOpen, Clock, Send, RotateCcw } from 'lucide-react';
import { ConstructionPanel } from '@/components/project/ConstructionPanel';
import { StageSOPChips } from '@/components/common/StageSOPChips';
import { ProjectFollowupsPanel } from '@/components/followups/ProjectFollowupsPanel';
import { StageSignoffPanel } from '@/components/project/StageSignoffPanel';
import { VisitReconciliationStrip } from '@/components/wo/VisitReconciliationStrip';
import { QualityChecksPanel, QcBadge } from '@/components/quality/QualityChecksPanel';

export default function ProjectDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(
    location.hash === '#quality' ? 'quality' : 'board');
  useEffect(() => {
    // React to same-page hash changes (e.g. Sign-off tab's "Open panel" link).
    if (location.hash === '#quality') setActiveTab('quality');
  }, [location.hash]);
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [signoffs, setSignoffs] = useState([]);

  const [notes, setNotes] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [linkRegistry, setLinkRegistry] = useState([]);
  const [newNote, setNewNote] = useState({ stage: '', body: '', is_decision: false });

  const load = () => api.get(`/projects/${id}`).then(r => {
    setData(r.data);
  }).catch(e => toast.error(parseErr(e)));
  const loadSignoffs = () => api.get(`/projects/${id}/stage-signoffs`)
    .then(r => setSignoffs(r.data.signoffs || []))
    .catch(() => {});
  const loadNotes = async () => {
    try {
      const [n, d, l] = await Promise.all([
        api.get(`/projects/${id}/notes`),
        api.get(`/projects/${id}/decisions`),
        api.get(`/projects/${id}/link_registry`),
      ]);
      setNotes(n.data || []); setDecisions(d.data || []); setLinkRegistry(l.data || []);
    } catch (e) { if (e?.response?.status !== 403) console.debug('project-notes load skipped:', e?.response?.status || e); }
  };
  useEffect(() => { load(); loadNotes(); loadSignoffs(); }, [id]);

  const saveNote = async () => {
    if (!newNote.body.trim()) return;
    try {
      await api.post(`/projects/${id}/notes`, {
        parent_type: newNote.stage ? 'stage' : 'project',
        stage: newNote.stage || undefined,
        body: newNote.body, is_decision: newNote.is_decision,
      });
      setNewNote({ stage: '', body: '', is_decision: false });
      toast.success('Note saved'); loadNotes();
    } catch (e) { toast.error(parseErr(e)); }
  };

  if (!data) return <AppShell><div className="p-6">Loading…</div></AppShell>;
  const p = data.project;

  const advance = async () => {
    try { await api.post(`/projects/${id}/advance_stage`); toast.success('Advanced to next stage'); load(); loadSignoffs(); }
    catch (e) { toast.error(parseErr(e)); }
  };

  const signoffChip = (row) => {
    if (!row) return <span className="chip"><Clock size={10} className="mr-1" />Not started</span>;
    const map = {
      not_started:         { c: 'chip', label: 'Not started', I: Clock },
      in_progress:         { c: 'chip chip-info', label: 'In progress', I: Clock },
      awaiting_signoff:    { c: 'chip chip-warning', label: 'Awaiting sign-off', I: Send },
      signed_off:          { c: 'chip chip-success', label: 'Signed off', I: CheckCircle2 },
      returned_for_update: { c: 'chip chip-warning', label: 'Returned', I: RotateCcw },
    };
    const s = map[row.status] || map.not_started;
    const I = s.I;
    return <span className={s.c}><I size={10} className="mr-1" />{s.label}</span>;
  };

  return (
    <AppShell breadcrumbs={[{ label: 'Projects', to: '/projects' }, { label: p.name }]}>
      <div className="p-6 max-w-[1400px] mx-auto" data-testid="project-detail">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <h1 className="font-heading text-2xl font-semibold">{p.name}</h1>
            <div className="text-[13px] text-[hsl(var(--foreground-muted))]">{p.client_name} · <span className="font-mono">{p.code}</span> · {p.site_address || ''}</div>
            {canSeeBillingName(user.role) && p.billing_name && (
              <div className="text-[12px] text-[hsl(var(--foreground-muted))] mt-0.5">Billing: <b>{p.billing_name}</b></div>
            )}
            {!canSeeBillingName(user.role) && p.billing_name_masked && (
              <div className="text-[12px] text-[hsl(var(--foreground-subtle))] mt-0.5 inline-flex items-center gap-1"><Lock size={11} />Billing name restricted</div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {p.drive_root_folder_url ? (
              <a href={p.drive_root_folder_url} target="_blank" rel="noreferrer" className="chip chip-info inline-flex items-center gap-1" data-testid="project-drive-link">
                <FolderOpen size={12} />Drive folder<ExternalLink size={10} />
              </a>
            ) : (
              ['owner', 'operations'].includes(user.role) && (
                <button data-testid="project-drive-set" onClick={async () => {
                  const url = window.prompt('Paste the Drive root folder URL for this project');
                  if (!url) return;
                  try { await api.put(`/projects/${id}/drive`, { drive_root_folder_url: url }); toast.success('Drive folder linked'); load(); }
                  catch (e) { toast.error(parseErr(e)); }
                }} className="chip chip-warning inline-flex items-center gap-1">
                  <FolderOpen size={12} />Set Drive folder
                </button>
              )
            )}
            <span className="chip chip-info">Current: {STAGE_LABEL[normalizeStage(p.current_stage)]}</span>
            <QcBadge_ProjectHeader projectId={id} stage={normalizeStage(p.current_stage)} />
            {p.stage_defaulted && (
              <span className="chip chip-warning text-[10px]" data-testid="pd-stage-defaulted"
                    title="No stage set on this project — the app defaulted to Strategy & Concept. Press Advance stage below to progress it.">UNSTAGED</span>
            )}
            {['owner', 'operations', 'dm_creative', 'dm_technical', 'dm_architecture'].includes(user.role) && (
              <Button size="sm" onClick={advance} data-testid="advance-stage"><PlayCircle size={14} className="mr-1" />Advance stage</Button>
            )}
          </div>
        </div>

        <div className="mb-4" data-testid="project-sop-strip">
          <StageSOPChips stage={normalizeStage(p.current_stage)} testId="pd-sop" />
        </div>

        <div className="mb-4">
          <VisitReconciliationStrip projectId={id} />
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="project-tabs">
          <TabsList>
            <TabsTrigger value="board" data-testid="tab-board">Stage board</TabsTrigger>
            <TabsTrigger value="milestones" data-testid="tab-milestones">Milestones</TabsTrigger>
            <TabsTrigger value="signoff" data-testid="tab-signoff">Stage sign-off</TabsTrigger>
            <TabsTrigger value="construction" data-testid="tab-construction">Construction &amp; Closure</TabsTrigger>
            <TabsTrigger value="followups" data-testid="tab-followups">Follow-ups</TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
            <TabsTrigger value="decisions" data-testid="tab-decisions">Decisions</TabsTrigger>
            <TabsTrigger value="links" data-testid="tab-links">Link registry</TabsTrigger>
            <TabsTrigger value="quality" data-testid="tab-quality">Quality checks</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="board">
            <div data-testid="project-stage-board" className="grid grid-flow-col auto-cols-[minmax(200px,1fr)] gap-3 overflow-x-auto pb-2 mt-3">
              {STAGES.map(s => {
                const row = signoffs.find(r => r.stage === s.key);
                const isCurrent = normalizeStage(p.current_stage) === s.key;
                return (
                  <div key={s.key} className="kb-col" data-testid={`board-column-${s.key}`}>
                    <div className="kb-col-header">
                      <div className="kb-col-title">{s.label}</div>
                      {isCurrent && <span className="chip chip-accent">Current</span>}
                    </div>
                    <div className="space-y-2">
                      <div className="kb-card">
                        <div className="text-[12px] text-[hsl(var(--foreground-muted))] uppercase tracking-wide">Sign-off</div>
                        <div className="mt-1">{signoffChip(row)}</div>
                        {row?.signed_off_at && (
                          <div className="text-[11px] text-[hsl(var(--foreground-subtle))] mt-1">
                            by {row.signed_off_by}<br />{fmtDate(row.signed_off_at)}
                          </div>
                        )}
                        {row?.status === 'returned_for_update' && (
                          <div className="text-[11px] text-[hsl(var(--foreground-subtle))] mt-1 whitespace-pre-wrap truncate">
                            {row.returned_reason}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="milestones">
            <div className="border rounded-xl bg-[hsl(var(--card))] overflow-x-auto mt-3" style={{ borderColor: 'hsl(var(--border))' }}>
              <table className="tbl" data-testid="milestones-table">
                <thead>
                  <tr><th>#</th><th>Milestone</th><th>Trigger</th><th>%</th><th>Amount</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {data.milestones.map((m, i) => (
                    <tr key={m.id} data-testid={`milestone-row-${m.id}`}>
                      <td className="tabular-nums text-[hsl(var(--foreground-subtle))]">{i + 1}</td>
                      <td>{m.name}</td>
                      <td className="text-[12.5px] text-[hsl(var(--foreground-muted))]">{m.trigger}</td>
                      <td className="tabular-nums">{m.percentage != null ? `${m.percentage}%` : (m.amount_masked ? <MaskedAmount masked /> : '—')}</td>
                      <td><MaskedAmount amount={m.amount_inr} masked={m.amount_masked} /></td>
                      <td><span className={`chip ${m.status === 'paid' ? 'chip-success' : m.status === 'invoiced' ? 'chip-info' : m.status === 'invoice_triggered' ? 'chip-warning' : ''}`}>{m.status.replace('_', ' ')}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!canSeeAmounts(user.role) && (
                <div className="px-4 py-2 text-[12px] text-[hsl(var(--foreground-muted))] border-t" style={{ borderColor: 'hsl(var(--border))' }}>
                  Amounts are visible only to Owner, Accounts, and Ops (read-only). You see milestone names and status.
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="signoff">
            <StageSignoffPanel
              projectId={id}
              currentStage={normalizeStage(p.current_stage)}
              onAdvance={() => { load(); loadSignoffs(); }}
            />
          </TabsContent>

          <TabsContent value="construction">
            <ConstructionPanel projectId={id} />
          </TabsContent>

          <TabsContent value="followups">
            <ProjectFollowupsPanel projectId={id} />
          </TabsContent>

          <TabsContent value="notes">
            <div className="card p-4 mt-3" data-testid="notes-panel">
              <div className="font-heading font-semibold mb-2">New note</div>
              <div className="grid grid-cols-[160px_1fr] gap-2 mb-2">
                <select className="h-9 border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }} value={newNote.stage} onChange={e => setNewNote(n => ({ ...n, stage: e.target.value }))} data-testid="note-stage">
                  <option value="">Project-level note</option>
                  {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                <label className="flex items-center gap-2 text-[13px]"><Checkbox checked={newNote.is_decision} onCheckedChange={v => setNewNote(n => ({ ...n, is_decision: !!v }))} data-testid="note-decision" /> Flag as Decision</label>
              </div>
              <Textarea rows={3} placeholder="Write the note. Drive links pasted here are auto-registered." value={newNote.body} onChange={e => setNewNote(n => ({ ...n, body: e.target.value }))} data-testid="note-body" />
              <div className="mt-2 flex justify-end"><Button onClick={saveNote} size="sm" data-testid="note-save"><MessageSquarePlus size={13} className="mr-1" />Save note</Button></div>
            </div>
            <div className="space-y-2 mt-4" data-testid="notes-list">
              {notes.length === 0 && <div className="text-[13px] text-[hsl(var(--foreground-muted))]">No notes yet.</div>}
              {notes.map(n => (
                <div key={n.id} className="card p-3" data-testid={`note-item-${n.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] font-medium">{n.author_name}</span>
                    <span className="chip">{n.author_role}</span>
                    {n.parent_type === 'stage' && n.stage && <span className="chip chip-info">{STAGE_LABEL[n.stage]}</span>}
                    {n.is_decision && <span className="chip chip-accent"><Star size={10} className="mr-1" />Decision</span>}
                    <span className="ml-auto text-[11.5px] text-[hsl(var(--foreground-subtle))]">{fmtDateTime(n.created_at)}</span>
                  </div>
                  <div className="mt-2 text-[13.5px] whitespace-pre-wrap">{n.body}</div>
                  {(n.drive_links || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {n.drive_links.map((L, i) => (
                        <a key={i} href={L.url} target="_blank" rel="noreferrer" className="chip chip-info inline-flex items-center gap-1">
                          <LinkIcon size={10} />{L.kind || 'file'}<ExternalLink size={10} />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="decisions">
            <div className="space-y-2 mt-3" data-testid="decisions-list">
              {decisions.length === 0 && <div className="text-[13px] text-[hsl(var(--foreground-muted))]">No decisions flagged yet.</div>}
              {decisions.map(d => (
                <div key={d.id} className="card p-3">
                  <div className="flex items-center gap-2">
                    <span className="chip chip-accent"><Star size={10} className="mr-1" />Decision</span>
                    <span className="text-[13px] font-medium">{d.author_name}</span>
                    <span className="ml-auto text-[11.5px] text-[hsl(var(--foreground-subtle))]">{fmtDateTime(d.created_at)}</span>
                  </div>
                  <div className="mt-2 text-[13.5px] whitespace-pre-wrap">{d.body}</div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="links">
            <div className="card overflow-x-auto mt-3" data-testid="link-registry">
              <table className="tbl">
                <thead><tr><th>Link</th><th>Kind</th><th>Added by</th><th>When</th></tr></thead>
                <tbody>
                  {linkRegistry.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-[hsl(var(--foreground-muted))]">No Drive links registered yet. Paste one in a note.</td></tr>}
                  {linkRegistry.map(L => (
                    <tr key={L.id}>
                      <td className="font-mono text-[12px] max-w-[400px] truncate"><a href={L.url} target="_blank" rel="noreferrer" className="text-[hsl(var(--primary))] underline">{L.url}</a></td>
                      <td>{L.kind}</td>
                      <td className="text-[12.5px]">{L.added_by}</td>
                      <td className="text-[12px] text-[hsl(var(--foreground-muted))]">{fmtDate(L.added_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="activity">
            <div className="border rounded-xl bg-[hsl(var(--card))] p-4 mt-3" style={{ borderColor: 'hsl(var(--border))' }}>
              <div className="font-heading font-semibold">Exceptions</div>
              {data.exceptions.length === 0 && <div className="text-[13px] text-[hsl(var(--foreground-muted))] mt-2">No exceptions logged.</div>}
              {data.exceptions.map(x => (
                <div key={x.id} className="border rounded-md p-3 mt-2" style={{ borderColor: 'hsl(var(--border))' }}>
                  <div className="text-[13px] font-medium">{x.reason}</div>
                  <div className="text-[11.5px] text-[hsl(var(--foreground-subtle))] mt-0.5">{fmtDate(x.created_at)}</div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="quality">
            <div className="mt-3 space-y-3" data-testid="pd-quality-tab">
              <StageQualityTabs projectId={id} currentStage={normalizeStage(p.current_stage)} />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

// ---- Optional Quality Checks inside Project detail ----
function StageQualityTabs({ projectId, currentStage }) {
  const [stage, setStage] = useState(currentStage || 'strategy_concept');
  useEffect(() => { if (currentStage) setStage(currentStage); }, [currentStage]);
  return (
    <>
      <div className="flex flex-wrap gap-1.5" data-testid="pd-quality-stage-switch">
        {STAGES.map(s => (
          <button key={s.key} type="button"
                  onClick={() => setStage(s.key)}
                  className={`chip text-[12px] ${stage === s.key ? 'chip-accent' : ''}`}
                  data-testid={`pd-quality-stage-${s.key}`}>
            {s.label}
            {s.key === currentStage && <span className="ml-1 text-[10px] opacity-70">current</span>}
          </button>
        ))}
      </div>
      <QualityChecksPanel projectId={projectId} stage={stage} />
    </>
  );
}

// ---- Header badge — just the QC completion pill ----
function QcBadge_ProjectHeader({ projectId, stage }) {
  return <QualityChecksPanel projectId={projectId} stage={stage} compact />
}
